import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useMemo, Children, cloneElement, useEffect, isValidElement } from 'react';
import styled from 'styled-components';
import { useMenu } from './Menu.context.js';
import { MenuItem } from './MenuItem.js';
import { MenuSection } from './MenuSection.js';
import { menu } from './Menu.tokens.js';
import { spacingsTemplate } from '@equinor/eds-utils';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["addCloseMenuOnClickIndex", "children"];
var List = styled.div.withConfig({
  displayName: "MenuList__List",
  componentId: "sc-104rzof-0"
})(["position:relative;list-style:none;display:flex;flex-direction:column;margin:0;", " li:first-child{z-index:3;}"], spacingsTemplate(menu.spacings));
function isIndexable(item) {
  if ( /*#__PURE__*/isValidElement(item) && !item.props.disabled && item.type === MenuItem) return true;
  return false;
}
function closeMenuOnClick(item) {
  if ( /*#__PURE__*/isValidElement(item) && item.type === MenuItem && item.props.closeMenuOnClick !== false) return true;
  return false;
}
var MenuList = /*#__PURE__*/forwardRef(function MenuList(_ref, ref) {
  var addCloseMenuOnClickIndex = _ref.addCloseMenuOnClickIndex,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useMenu = useMenu(),
    focusedIndex = _useMenu.focusedIndex,
    setFocusedIndex = _useMenu.setFocusedIndex,
    initialFocus = _useMenu.initialFocus;
  var index = -1;
  var focusableIndexs = useMemo(function () {
    return [];
  }, []);
  var updatedChildren = useMemo(function () {
    return Children.map(children, function (child) {
      if (!child) return child;
      if (child.type === MenuSection) {
        index++;
        var menuSectionIndex = index;
        var updatedGrandChildren = Children.map(child.props.children, function (grandChild) {
          index++;
          if (isIndexable(grandChild)) focusableIndexs.push(index);
          return /*#__PURE__*/cloneElement(grandChild, {
            index: index
          });
        });
        return /*#__PURE__*/cloneElement(child, {
          index: menuSectionIndex
        }, updatedGrandChildren);
      } else {
        index++;
        if (isIndexable(child)) focusableIndexs.push(index);
        if (closeMenuOnClick(child)) {
          addCloseMenuOnClickIndex(index);
        }
        return /*#__PURE__*/cloneElement(child, {
          index: index
        });
      }
    });
  }, [children, focusableIndexs, index, addCloseMenuOnClickIndex]);
  var firstFocusIndex = focusableIndexs[0];
  var lastFocusIndex = focusableIndexs[focusableIndexs.length - 1];
  useEffect(function () {
    if (initialFocus === 'first') {
      setFocusedIndex(firstFocusIndex);
    }
    if (initialFocus === 'last') {
      setFocusedIndex(lastFocusIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFocus, firstFocusIndex, lastFocusIndex]);
  var handleMenuItemChange = function handleMenuItemChange(direction, fallbackIndex) {
    var i = direction === 'down' ? 1 : -1;
    var currentFocus = focusableIndexs.indexOf(focusedIndex);
    var nextMenuItem = focusableIndexs[currentFocus + i];
    var nextFocusedIndex = typeof nextMenuItem === 'undefined' ? fallbackIndex : nextMenuItem;
    setFocusedIndex(nextFocusedIndex);
  };
  var handleKeyPress = function handleKeyPress(event) {
    var key = event.key;
    event.stopPropagation();
    if (key === 'ArrowDown') {
      event.preventDefault();
      handleMenuItemChange('down', firstFocusIndex);
    }
    if (key === 'ArrowUp') {
      event.preventDefault();
      handleMenuItemChange('up', lastFocusIndex);
    }
  };
  return /*#__PURE__*/jsx(List, _objectSpread(_objectSpread({
    onKeyDown: handleKeyPress,
    role: "menu"
  }, rest), {}, {
    ref: ref,
    children: updatedChildren
  }));
});

// MenuList.displayName = 'EdsMenuList'

export { MenuList };
